/**
 * trolleySlotHelpers.js
 *
 * Shared helpers for the cart-slot item picker used in trolleyLoading and
 * trolleyLoadingAka.  A cart slot's itemId can now be either:
 *   (a) a numeric item-master id, or
 *   (b) one of the meal-category codes returned by GET /galley/mealMenuPlan/configs
 *       e.g. "breakfast", "lunch", …
 */

/**
 * Build grouped react-select options for the slot item picker.
 *
 * @param {Array<{_id: string, itemName: string}>} items   – from api/items/enumerate
 * @param {Array<{id: string, label: string}>}    mealCategories – from mealMenuPlan/configs
 * @returns {Array}  react-select options array (may contain group objects)
 */
export function toSlotPickerOptions(items, mealCategories) {
  const mealGroup = {
    label: 'Meal Categories',
    options: (mealCategories || []).map((cat) => ({
      value: cat.id,
      label: cat.label,
      isMealCategory: true,
    })),
  };

  const itemGroup = {
    label: 'Items',
    options: (items || [])
      .filter((it) => it._id !== null && it._id !== undefined && it.itemName !== 'Select Item')
      .map((it) => ({
        value: it._id,
        label: it.itemName,
        isMealCategory: false,
      })),
  };

  // If no meal categories, return flat list for backward compat
  if (!mealGroup.options.length) return itemGroup.options;

  return [mealGroup, itemGroup];
}

/**
 * Given a saved itemId string, resolve it to a react-select {value, label, isMealCategory}
 * object so the picker shows the correct default value on read-back.
 *
 * Resolution order:
 *  1. item-master match (compare as strings)
 *  2. meal-category id match
 *  3. return null (unknown / bad id)
 *
 * @param {string|number}                          itemId
 * @param {Array<{_id: string, itemName: string}>} items
 * @param {Array<{id: string, label: string}>}     mealCategories
 * @returns {{ value: string, label: string, isMealCategory: boolean } | null}
 */
export function resolveSlotItemLabel(itemId, items, mealCategories) {
  if (itemId === null || itemId === undefined || itemId === '') return null;

  const idStr = String(itemId);

  // 1. item-master
  const found = (items || []).find((it) => String(it._id) === idStr);
  if (found) return { value: found._id, label: found.itemName, isMealCategory: false };

  // 2. meal category
  const cat = (mealCategories || []).find((c) => c.id === idStr);
  if (cat) return { value: cat.id, label: cat.label, isMealCategory: true };

  return null;
}

/**
 * formatOptionLabel for react-select – renders a small "Meal" badge next to
 * meal-category options so they are visually distinct from real items.
 */
export function formatSlotOptionLabel(option) {
  if (option.isMealCategory) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            background: 'rgba(76,195,230,0.18)',
            color: '#4cc3e6',
            border: '1px solid rgba(76,195,230,0.4)',
            borderRadius: 4,
            padding: '1px 5px',
            letterSpacing: 0.3,
            flexShrink: 0,
          }}
        >
          MEAL
        </span>
        {option.label}
      </span>
    );
  }
  return option.label;
}
